"use client";

import Link from "next/link";
import { useRef, useState } from "react";
import { createDemandSignal } from "@/app/actions/demand";

export function DemandCaptureForm({
  trade,
  tradeName,
  postcode,
}: {
  trade: string | null;
  tradeName: string | null;
  postcode: string | null;
}) {
  const [email, setEmail] = useState("");
  const [done, setDone] = useState(false);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const submitting = useRef(false);

  const label = tradeName ? tradeName.toLowerCase() : "tradesperson";
  const place = postcode ? ` near ${postcode}` : " in your area";

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (submitting.current) return;
    submitting.current = true;
    setError("");
    setLoading(true);
    try {
      const result = await createDemandSignal({ email, trade, postcode });
      if (result.error) setError(result.error);
      else setDone(true);
    } catch {
      setError("Something went wrong. Please try again.");
    } finally {
      setLoading(false);
      submitting.current = false;
    }
  }

  return (
    <div className="mt-8 pt-6 border-t border-stone/20 text-left max-w-md mx-auto">
      {done ? (
        <div className="p-4 bg-sage-pale border border-sage-light/50 rounded-sm text-sm text-forest">
          Thanks. We&apos;ll email you the moment a {label} joins{place}. Keep an eye
          on your inbox (and your spam folder, just in case).
        </div>
      ) : (
        <>
          <p className="text-sm font-semibold text-charcoal mb-1">
            Want us to let you know when a {label} joins{place}?
          </p>
          <p className="text-sm text-text-muted mb-3">
            Leave your email and we&apos;ll get in touch as soon as there&apos;s a match. No spam, ever.
          </p>
          <form onSubmit={handleSubmit} className="flex flex-col sm:flex-row gap-2">
            <input
              type="email"
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="you@example.com"
              aria-label="Your email address"
              className="flex-1 px-4 py-2.5 bg-cream border border-stone rounded-sm text-sm focus:outline-none focus:ring-2 focus:ring-forest/20 focus:border-forest"
            />
            <button
              type="submit"
              disabled={loading}
              className="px-5 py-2.5 bg-forest text-white font-semibold rounded-sm hover:bg-forest-light transition-colors text-sm disabled:opacity-50 whitespace-nowrap"
            >
              {loading ? "Saving..." : "Notify me"}
            </button>
          </form>
          {error && <p className="mt-2 text-sm text-error">{error}</p>}
          <p className="mt-3 text-xs text-text-muted">
            Know a good {label}?{" "}
            <Link href="/for-tradespeople" className="text-forest font-medium hover:underline">
              Invite them to join for free
            </Link>
            .
          </p>
        </>
      )}
    </div>
  );
}
